"use client";

import Image from "next/image";
import Link from "next/link";
import { useRef } from "react";
import { useInView } from "framer-motion";
import Overline from "~/componentsDS/Typography/Overline";
import { cn } from "~/lib/utils";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";

type WhatWeTreatCardProps = {
    link: string;
    title: string;
    buttonBg: string;
    cardClassName?: string;
    imageUrl: string;
    imageSizes: string;
};

const WhatWeTreatCard = ({ link, title, buttonBg, cardClassName, imageUrl, imageSizes }: WhatWeTreatCardProps) => {
    const ref = useRef<HTMLAnchorElement | null>(null);
    const isInView = useInView(ref, { once: true, amount: "some" });
    const sendEvent = useTrackEvent();

    const handleClick = () => {
        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.ABOUT_INTERACTION,
                widget: "about_treatments",
                meta_data: {
                    click_text: title,
                },
            },
        });
    };

    return (
        <Link
            ref={ref}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            style={{ transition: "border-radius 1500ms cubic-bezier(0.22, 1, 0.36, 1)" }}
            className={cn(
                "group relative flex justify-center items-center overflow-hidden bg-trueBlack/5",
                isInView ? "rounded-3xl md:rounded-[52px]" : "rounded-[200px]",
                cardClassName
            )}

        >
            <div className="absolute inset-0">
                <Image
                    src={imageUrl}
                    alt={title}
                    fill
                    sizes={imageSizes}
                    quality={95}
                    className="
                        object-cover
                        transition-transform duration-700
                        ease-[cubic-bezier(0.22,1,0.36,1)]
                        group-hover:scale-[1.08]
                    "
                />
            </div>

            <div
                className="
                    absolute inset-0
                    bg-trueBlack/25
                    transition-opacity duration-700
                    opacity-0
                    ease-[cubic-bezier(0.22,1,0.36,1)]
                    group-hover:opacity-100
                "
            />

            <div className="absolute md:inset-0 flex items-center justify-center flex-wrap gap-[6px] md:gap-[10px]">
                <div
                    className="flex items-center rounded-full px-4 md:px-6 h-[56px] md:h-[60px] backdrop-blur-[6px]"
                    style={{ backgroundColor: buttonBg }}
                >
                    <Overline size="sm" sizes={{ md: "lg" }} className="text-pureWhite">{title}</Overline>
                </div>

                <div
                    className="
                        flex justify-center items-center rounded-full w-[51px] h-[51px] shrink-0 backdrop-blur-[6px]
                        transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
                        group-hover:translate-x-2
                    "
                    style={{ backgroundColor: buttonBg }}
                >
                    <div className="relative aspect-[17/15] w-[13px] md:w-[17px]">
                        <Image
                            src="https://res.cloudinary.com/dirscuuhp/image/upload/v1777542420/design_system/icons/arrow-right-white_bctlb9.svg"
                            alt="Arrow Right"
                            fill
                            sizes="17px"
                            className="object-cover"
                        />
                    </div>
                </div>
            </div>
        </Link>
    )
};

export default WhatWeTreatCard;
